import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { fmtTL, fmtTLx, fmtMoney, fmtPay, API } from "@/lib/api";
import { Plus, Trash2, X, Eye, Upload, IdCard, MapPin, Phone, Calendar, User, Image as ImageIcon, Loader2, Pencil, KeyRound, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const ASGARI = 22104;
const emptyForm = { name: "", role: "", branch_id: "", base_salary: ASGARI, extras: [], tc_no: "", phone: "", birth_date: "", hire_date: "", address: "", photo_path: null, id_front_path: null, id_back_path: null, prim: 0, avans: 0, kasa_acigi: 0, prim_desc: "", avans_desc: "", kasa_desc: "", pin: "", currency: "TRY" };

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const fmtPeriod = (p) => {
  if (!p) return "—";
  const [y, m] = p.split("-");
  return `${TR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

// Authenticated image loader (cookie is sent same-origin)
function AuthImage({ path, className, alt, fallback }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let objUrl;
    if (!path) { setUrl(null); return; }
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { if (!revoked) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } })
      .catch(() => {});
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  if (!url) return fallback || null;
  return <img src={url} className={className} alt={alt} />;
}

export default function Personnel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [quick, setQuick] = useState(null);
  const [quickLabel, setQuickLabel] = useState("");
  const [quickAmount, setQuickAmount] = useState("");
  const [onlyBase, setOnlyBase] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState({ photo: false, id_front: false, id_back: false });

  const { data: personnel = [] } = useQuery({ queryKey: ["personnel"], queryFn: async () => (await api.get("/personnel")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["personnel"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const createM = useMutation({
    mutationFn: (p) => api.post("/personnel", p),
    onSuccess: () => { invalidate(); setOpen(false); toast.success("Personel eklendi"); setForm(emptyForm); },
  });
  const updateM = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/personnel/${id}`, payload),
    onSuccess: () => { invalidate(); setOpen(false); setEditId(null); setForm(emptyForm); toast.success("Personel güncellendi"); },
    onError: () => toast.error("Güncelleme başarısız"),
  });

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name, role: p.role || "", branch_id: p.branch_id || "",
      base_salary: p.base_salary, extras: p.extras || [],
      tc_no: p.tc_no || "", phone: p.phone || "", birth_date: p.birth_date || "", hire_date: p.hire_date || "", address: p.address || "",
      photo_path: p.photo_path || null, id_front_path: p.id_front_path || null, id_back_path: p.id_back_path || null,
      prim: p.prim || 0, avans: p.avans || 0, kasa_acigi: p.kasa_acigi || 0, prim_desc: p.prim_desc || "", avans_desc: p.avans_desc || "", kasa_desc: p.kasa_desc || "", pin: p.pin || "", currency: p.currency || "TRY",
      absent_days: Math.max(0, (p.absent_days || 0) - (p.record_absent_days || 0)),
    });
    setOpen(true);
  };
  const delM = useMutation({ mutationFn: (id) => api.delete(`/personnel/${id}`), onSuccess: () => { invalidate(); toast.success("Personel silindi"); setDetail(null); } });

  const quickM = useMutation({
    mutationFn: ({ mode, p, label, amount, op }) => {
      const payload = {
        name: p.name, role: p.role, branch_id: p.branch_id, base_salary: p.base_salary,
        extras: p.extras || [], tc_no: p.tc_no, phone: p.phone, birth_date: p.birth_date, hire_date: p.hire_date || "", address: p.address,
        photo_path: p.photo_path, id_front_path: p.id_front_path, id_back_path: p.id_back_path,
        absent_days: Math.max(0, (p.absent_days || 0) - (p.record_absent_days || 0)), prim: p.prim || 0, avans: p.avans || 0, kasa_acigi: p.kasa_acigi || 0,
        prim_desc: p.prim_desc || "", avans_desc: p.avans_desc || "", kasa_desc: p.kasa_desc || "", pin: p.pin || "", currency: p.currency || "TRY",
      };
      if (mode === "extra") payload.extras = [...payload.extras, { label, amount }];
      const key = { prim: "prim", avans: "avans", kasa: "kasa_acigi" }[mode];
      if (key) {
        payload[key] = op === "set" ? amount : payload[key] + amount;
        const dkey = { prim: "prim_desc", avans: "avans_desc", kasa: "kasa_desc" }[mode];
        if (label) payload[dkey] = label;
      }
      return api.put(`/personnel/${p.id}`, payload);
    },
    onSuccess: (_, v) => { invalidate(); setQuick(null); toast.success(v.mode === "extra" ? "Ek maaş eklendi" : v.mode === "prim" ? "Prim kaydedildi" : v.mode === "avans" ? "Avans kaydedildi" : "Kasa açığı kaydedildi"); },
    onError: () => toast.error("İşlem başarısız"),
  });

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const [closeOpen, setCloseOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const { data: previews = [], isLoading: prevLoading } = useQuery({
    queryKey: ["month-close-preview"],
    queryFn: async () => (await api.get("/month-close/preview")).data,
    enabled: closeOpen,
  });
  const { data: closures = [], isLoading: closLoading } = useQuery({
    queryKey: ["closures"],
    queryFn: async () => (await api.get("/closures")).data,
    enabled: histOpen,
  });
  const closeM = useMutation({
    mutationFn: () => api.post("/month-close"),
    onSuccess: (r) => { invalidate(); setCloseOpen(false); toast.success(`${r.data.closed} personel için ay kapanışı tamamlandı`); },
    onError: () => toast.error("Kapanış yapılamadı"),
  });
  const pendingClose = personnel.filter((p) => p.needs_close);
  const closuresByPeriod = closures.reduce((m, c) => { (m[c.period_month] = m[c.period_month] || []).push(c); return m; }, {});
  const baseTotal = personnel.reduce((s, p) => s + p.base_salary * (p.fx || 1), 0);
  const extraTotal = personnel.reduce((s, p) => s + p.extra_total * (p.fx || 1), 0);

  const addExtra = () => setForm((f) => ({ ...f, extras: [...f.extras, { label: "", amount: 0 }] }));
  const updExtra = (i, k, v) => setForm((f) => { const e = [...f.extras]; e[i][k] = k === "amount" ? parseFloat(v || 0) : v; return { ...f, extras: e }; });
  const rmExtra = (i) => setForm((f) => ({ ...f, extras: f.extras.filter((_, x) => x !== i) }));

  const uploadFile = async (file, kind) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [{ photo: "photo_path", id_front: "id_front_path", id_back: "id_back_path" }[kind]]: data.path }));
      toast.success(kind === "photo" ? "Fotoğraf yüklendi" : kind === "id_front" ? "Kimlik ön yüz yüklendi" : "Kimlik arka yüz yüklendi");
    } catch (e) {
      toast.error("Yükleme başarısız");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, base_salary: parseFloat(form.base_salary), prim: parseFloat(form.prim || 0), avans: parseFloat(form.avans || 0), kasa_acigi: parseFloat(form.kasa_acigi || 0) };
    if (editId) updateM.mutate({ id: editId, payload });
    else createM.mutate(payload);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Personel & Maaş</h1>
          <p className="text-slate-400 text-sm mt-1">Kimlik bilgileri, adres ve maaş takibi</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="history-button" onClick={() => setHistOpen(true)} className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
            <History className="w-4 h-4" /> Geçmiş İşlemler
          </button>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyForm); setEditId(null); } }}>
          <DialogTrigger asChild>
            <button data-testid="add-personnel-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
              <Plus className="w-4 h-4" /> Personel Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1220] border-slate-800 text-white max-h-[88vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-5">
              {/* Photo + ID upload */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-24 h-24 rounded-2xl bg-slate-900 border border-slate-700 overflow-hidden flex items-center justify-center">
                    <AuthImage path={form.photo_path} className="w-full h-full object-cover" alt="foto" fallback={<User className="w-8 h-8 text-slate-600" />} />
                  </div>
                  <label className="text-xs text-blue-400 cursor-pointer hover:text-blue-300 flex items-center gap-1">
                    {uploading.photo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Fotoğraf
                    <input data-testid="photo-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files[0], "photo")} />
                  </label>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-slate-900 border border-slate-700 p-2.5">
                    <div className="w-full h-16 rounded-lg overflow-hidden flex items-center justify-center bg-slate-800">
                      <AuthImage path={form.id_front_path} className="w-full h-full object-cover" alt="kimlik ön" fallback={<IdCard className="w-6 h-6 text-slate-600" />} />
                    </div>
                    <label className="text-xs text-blue-400 cursor-pointer hover:text-blue-300 flex items-center gap-1">
                      {uploading.id_front ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Kimlik Ön
                      <input data-testid="id-front-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files[0], "id_front")} />
                    </label>
                  </div>
                  <div className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-slate-900 border border-slate-700 p-2.5">
                    <div className="w-full h-16 rounded-lg overflow-hidden flex items-center justify-center bg-slate-800">
                      <AuthImage path={form.id_back_path} className="w-full h-full object-cover" alt="kimlik arka" fallback={<IdCard className="w-6 h-6 text-slate-600 rotate-180" />} />
                    </div>
                    <label className="text-xs text-blue-400 cursor-pointer hover:text-blue-300 flex items-center gap-1">
                      {uploading.id_back ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Kimlik Arka
                      <input data-testid="id-back-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files[0], "id_back")} />
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Ad Soyad</label>
                  <input data-testid="personnel-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Görev</label>
                  <input data-testid="personnel-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">T.C. Kimlik No</label>
                  <input data-testid="personnel-tc-input" value={form.tc_no} maxLength={11} onChange={(e) => setForm({ ...form, tc_no: e.target.value.replace(/\D/g, "") })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="11 hane" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Telefon</label>
                  <input data-testid="personnel-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="05xx xxx xx xx" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Doğum Tarihi</label>
                  <input data-testid="personnel-birth-input" type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 [color-scheme:dark]" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Portal PIN (personel girişi)</label>
                  <input data-testid="personnel-pin-input" value={form.pin} maxLength={6} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. 1234" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">İşe Giriş Tarihi</label>
                <input data-testid="personnel-hire-input" type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 [color-scheme:dark]" />
                <div className="text-[11px] text-slate-500 mt-1">Yıllık izin hakkı kıdeme göre otomatik hesaplanır (1-5 yıl: 14, 5-15 yıl: 20, 15+ yıl: 26 gün). İzin ücretlidir, maaştan düşülmez.</div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Şube</label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="personnel-branch-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Adres</label>
                <textarea data-testid="personnel-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 resize-none" placeholder="Açık adres" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Para Birimi</label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="personnel-currency-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                      <SelectItem value="TRY" data-testid="currency-option-try">TL (₺)</SelectItem>
                      <SelectItem value="USD" data-testid="currency-option-usd">Dolar ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-widest text-slate-400">{form.currency === "USD" ? "Maaş ($)" : "Asgari Ücret (₺)"}</label>
                  <input data-testid="personnel-base-input" required type="number" step="0.01" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
                </div>
              </div>
              {form.currency === "USD" && <div className="text-[11px] text-slate-500 -mt-1">Maaş ve ek ödemeler dolar; prim, avans ve kasa açığı TL girilir, Harem alış kuruyla dolara çevrilip maaştan düşülür.</div>}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Prim (₺)</label>
                  <input data-testid="personnel-prim-input" type="number" min="0" step="0.01" value={form.prim} onChange={(e) => setForm({ ...form, prim: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="0" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Avans (₺)</label>
                  <input data-testid="personnel-avans-input" type="number" min="0" step="0.01" value={form.avans} onChange={(e) => setForm({ ...form, avans: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="0" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400">Kasa Açığı (₺)</label>
                  <input data-testid="personnel-kasa-input" type="number" min="0" step="0.01" value={form.kasa_acigi} onChange={(e) => setForm({ ...form, kasa_acigi: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="0" />
                </div>
              </div>
              <div className="text-[11px] text-slate-500 -mt-1">Prim, avans, kasa açığı ve devamsızlık ay sonu kapanışında (onayınızla) sıfırlanır; asgari ücret ve ek maaş sabittir.</div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-widest text-slate-400">Ek Ödemeler ({form.currency === "USD" ? "$" : "₺"})</label>
                  <button type="button" data-testid="add-extra-button" onClick={addExtra} className="text-blue-400 text-xs font-medium hover:text-blue-300">+ Ekle</button>
                </div>
                <div className="mt-2 space-y-2">
                  {form.extras.map((ex, i) => (
                    <div key={i} className="flex gap-2">
                      <input placeholder="Açıklama" value={ex.label} onChange={(e) => updExtra(i, "label", e.target.value)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
                      <input placeholder="Tutar" type="number" value={ex.amount} onChange={(e) => updExtra(i, "amount", e.target.value)} className="w-28 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
                      <button type="button" onClick={() => rmExtra(i)} className="px-2 text-slate-500 hover:text-rose-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <button data-testid="personnel-submit-button" type="submit" disabled={createM.isPending || updateM.isPending} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-lg text-sm">{editId ? "Güncelle" : "Kaydet"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Ay sonu kapanış banner */}
      {pendingClose.length > 0 && (
        <div data-testid="month-close-banner" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="flex-1 text-sm text-amber-200">
            <b>Ay sonu kapanışı bekliyor:</b> {pendingClose.length} personel için prim, avans ve devamsızlık sıfırlanacak. Mevcut değerler geçmiş işlemlere arşivlenir.
          </div>
          <button data-testid="month-close-open-button" onClick={() => setCloseOpen(true)} className="shrink-0 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors">
            Kapanışı Görüntüle
          </button>
        </div>
      )}

      {/* Totals + toggle */}
      <div className="glass rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex gap-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Toplam Asgari Ücret</div>
            <div className="text-2xl font-heading font-bold text-blue-400 tabular" data-testid="base-total">{fmtTL(baseTotal)}</div>
          </div>
          <div className={onlyBase ? "opacity-30 transition-opacity" : "transition-opacity"}>
            <div className="text-xs uppercase tracking-widest text-slate-400">Toplam Ek Ödeme</div>
            <div className="text-2xl font-heading font-bold text-amber-400 tabular" data-testid="extra-total">{fmtTL(extraTotal)}</div>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer bg-slate-800/60 rounded-xl px-4 py-2.5">
          <Eye className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-300">Sadece Asgari Ücreti Göster</span>
          <Switch data-testid="only-base-toggle" checked={onlyBase} onCheckedChange={setOnlyBase} />
        </label>
      </div>

      {/* Personnel list */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {personnel.map((p) => (
          <div key={p.id} data-testid={`personnel-card-${p.id}`} onClick={() => setDetail(p)} className="glass rounded-2xl p-5 cursor-pointer hover:border-blue-600/40 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-600/15 text-blue-400 flex items-center justify-center font-heading font-bold overflow-hidden">
                  <AuthImage path={p.photo_path} className="w-full h-full object-cover" alt={p.name} fallback={<span>{p.name[0]}</span>} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5">{p.name}{p.currency === "USD" && <span data-testid={`usd-badge-${p.id}`} className="text-[10px] font-bold bg-emerald-500/15 text-emerald-400 rounded px-1.5 py-0.5">USD</span>}</div>
                  <div className="text-xs text-slate-400">{p.role || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-personnel-${p.id}`} onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="text-slate-500 hover:text-blue-400 transition-colors"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-personnel-${p.id}`} onClick={(e) => { e.stopPropagation(); delM.mutate(p.id); }} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-3 flex items-center gap-1"><MapPin className="w-3 h-3" /> {branchName(p.branch_id)}</div>

            <div className="mt-4 space-y-2 border-t border-slate-800 pt-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">{p.currency === "USD" ? "Maaş" : "Asgari Ücret"}</span>
                <span className="text-blue-400 font-mono font-medium">{fmtMoney(p.base_salary, p.currency)}</span>
              </div>
              {!onlyBase && p.extras.map((ex, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-slate-500">+ {ex.label}</span>
                  <span className="text-amber-400 font-mono">{fmtMoney(ex.amount, p.currency)}</span>
                </div>
              ))}
              {!onlyBase && (
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {p.extras.length === 0 && (
                    <button type="button" data-testid={`add-extra-quick-${p.id}`} onClick={(e) => { e.stopPropagation(); setQuick({ mode: "extra", p }); setQuickLabel(""); setQuickAmount(""); }} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
                      <Plus className="w-3 h-3" /> Ek Maaş
                    </button>
                  )}
                  <button type="button" data-testid={`add-prim-${p.id}`} onClick={(e) => { e.stopPropagation(); setQuick({ mode: "prim", p }); setQuickAmount(""); }} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors">
                    <Plus className="w-3 h-3" /> Prim
                  </button>
                  <button type="button" data-testid={`add-avans-${p.id}`} onClick={(e) => { e.stopPropagation(); setQuick({ mode: "avans", p }); setQuickAmount(""); }} className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1 transition-colors">
                    <Plus className="w-3 h-3" /> Avans
                  </button>
                  <button type="button" data-testid={`add-kasa-${p.id}`} onClick={(e) => { e.stopPropagation(); setQuick({ mode: "kasa", p }); setQuickAmount(""); }} className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1 transition-colors">
                    <Plus className="w-3 h-3" /> Kasa Açığı
                  </button>
                </div>
              )}
              {!onlyBase && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Toplam Maaş</span>
                  <span className="text-white font-mono font-medium">{fmtMoney(p.base_salary + p.extra_total, p.currency)}</span>
                </div>
              )}
              {!onlyBase && p.prim > 0 && (
                <div className="flex justify-between text-xs" data-testid={`prim-line-${p.id}`}>
                  <span className="text-slate-500">+ Prim{p.prim_desc ? ` · ${p.prim_desc}` : ""}</span>
                  <span className="text-violet-400 font-mono">{fmtTLx(p.prim, p)}</span>
                </div>
              )}
              {!onlyBase && p.absent_days > 0 && (
                <div className="flex justify-between text-xs" data-testid={`absence-deduction-${p.id}`}>
                  <span className="text-rose-400">− {p.absent_days} gün devamsızlık</span>
                  <span className="text-rose-400 font-mono">-{fmtMoney(p.absence_deduction, p.currency)}</span>
                </div>
              )}
              {!onlyBase && p.avans > 0 && (
                <div className="flex justify-between text-xs" data-testid={`avans-line-${p.id}`}>
                  <span className="text-rose-400">− Avans{p.avans_desc ? ` · ${p.avans_desc}` : ""}</span>
                  <span className="text-rose-400 font-mono">-{fmtTLx(p.avans, p)}</span>
                </div>
              )}
              {!onlyBase && p.kasa_acigi > 0 && (
                <div className="flex justify-between text-xs" data-testid={`kasa-line-${p.id}`}>
                  <span className="text-orange-400">− Kasa Açığı{p.kasa_desc ? ` · ${p.kasa_desc}` : ""}</span>
                  <span className="text-orange-400 font-mono">-{fmtTLx(p.kasa_acigi, p)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t border-slate-800 pt-2 mt-2">
                <span className="text-white font-semibold">{onlyBase ? "Asgari Toplam" : "Ödenecek Tutar"}</span>
                <span className="text-white font-heading font-bold tabular">{onlyBase ? fmtMoney(p.base_salary, p.currency) : fmtPay(p)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-[#0D1220] border-slate-800 text-white max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="personnel-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Personel Kimlik Kartı</DialogTitle></DialogHeader>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-slate-900 border border-slate-700 overflow-hidden flex items-center justify-center text-2xl font-heading font-bold text-blue-400">
                  <AuthImage path={detail.photo_path} className="w-full h-full object-cover" alt={detail.name} fallback={<span>{detail.name[0]}</span>} />
                </div>
                <div>
                  <div className="text-lg font-heading font-bold text-white">{detail.name}</div>
                  <div className="text-sm text-slate-400">{detail.role || "—"} · {branchName(detail.branch_id)}</div>
                </div>
              </div>

              <div className="space-y-3 mt-2">
                <Row icon={IdCard} label="T.C. Kimlik No" value={detail.tc_no || "—"} />
                <Row icon={KeyRound} label="Portal PIN" value={detail.pin || "Tanımsız"} />
                <Row icon={Phone} label="Telefon" value={detail.phone || "—"} />
                <Row icon={Calendar} label="Doğum Tarihi" value={detail.birth_date || "—"} />
                <Row icon={Calendar} label="İşe Giriş Tarihi" value={detail.hire_date || "—"} />
                <Row icon={MapPin} label="Adres" value={detail.address || "—"} />
              </div>

              {(detail.id_front_path || detail.id_back_path) && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1 mb-2"><ImageIcon className="w-3.5 h-3.5" /> Kimlik Görüntüsü</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Ön Yüz</div>
                      <div className="rounded-xl border border-slate-700 bg-slate-800 min-h-[80px] overflow-hidden flex items-center justify-center">
                        {detail.id_front_path
                          ? <AuthImage path={detail.id_front_path} className="w-full" alt="kimlik ön" fallback={<div className="text-slate-500 text-xs">Yükleniyor…</div>} />
                          : <IdCard className="w-6 h-6 text-slate-600" />}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Arka Yüz</div>
                      <div className="rounded-xl border border-slate-700 bg-slate-800 min-h-[80px] overflow-hidden flex items-center justify-center">
                        {detail.id_back_path
                          ? <AuthImage path={detail.id_back_path} className="w-full" alt="kimlik arka" fallback={<div className="text-slate-500 text-xs">Yükleniyor…</div>} />
                          : <IdCard className="w-6 h-6 text-slate-600" />}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-xl bg-slate-800/50 p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-400">{detail.currency === "USD" ? "Maaş" : "Asgari Ücret"}</span><span className="text-blue-400 font-mono">{fmtMoney(detail.base_salary, detail.currency)}</span></div>
                {detail.extras.map((ex, i) => (
                  <div key={i} className="flex justify-between text-xs"><span className="text-slate-500">+ {ex.label}</span><span className="text-amber-400 font-mono">{fmtMoney(ex.amount, detail.currency)}</span></div>
                ))}
                <div className="flex justify-between text-sm"><span className="text-slate-300">Toplam Maaş</span><span className="text-white font-mono font-medium">{fmtMoney(detail.base_salary + detail.extra_total, detail.currency)}</span></div>
                {detail.prim > 0 && (
                  <div className="flex justify-between text-xs"><span className="text-slate-500">+ Prim{detail.prim_desc ? ` · ${detail.prim_desc}` : ""} (ay sonu sıfırlanır)</span><span className="text-violet-400 font-mono">{fmtTLx(detail.prim, detail)}</span></div>
                )}
                {detail.absent_days > 0 && (
                  <div className="flex justify-between text-xs"><span className="text-rose-400">− {detail.absent_days} gün devamsızlık (günlük {fmtMoney(detail.daily_rate, detail.currency)}{detail.currency === "USD" ? ` · ₺${Math.round(detail.daily_rate_try).toLocaleString("tr-TR")} ÷ kur` : ""})</span><span className="text-rose-400 font-mono">-{fmtMoney(detail.absence_deduction, detail.currency)}</span></div>
                )}
                {detail.avans > 0 && (
                  <div className="flex justify-between text-xs"><span className="text-rose-400">− Avans{detail.avans_desc ? ` · ${detail.avans_desc}` : ""} (ay sonu sıfırlanır)</span><span className="text-rose-400 font-mono">-{fmtTLx(detail.avans, detail)}</span></div>
                )}
                {detail.kasa_acigi > 0 && (
                  <div className="flex justify-between text-xs" data-testid="detail-kasa-line"><span className="text-orange-400">− Kasa Açığı{detail.kasa_desc ? ` · ${detail.kasa_desc}` : ""} (ay sonu sıfırlanır)</span><span className="text-orange-400 font-mono">-{fmtTLx(detail.kasa_acigi, detail)}</span></div>
                )}
                <div className="flex justify-between text-sm border-t border-slate-700 pt-2"><span className="text-white font-semibold">Ödenecek Tutar</span><span data-testid="detail-total-pay" className="text-white font-heading font-bold">{fmtPay(detail)}</span></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick add dialog (ek maaş / prim / avans) */}
      <Dialog open={!!quick} onOpenChange={(v) => !v && setQuick(null)}>
        <DialogContent className="bg-[#0D1220] border-slate-800 text-white sm:max-w-sm" data-testid="quick-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {quick?.mode === "extra" ? "Ek Maaş Ekle" : quick?.mode === "prim" ? "Prim Ekle / Düzenle" : quick?.mode === "avans" ? "Avans Ekle / Düzenle" : "Kasa Açığı Ekle / Düzenle"} — {quick?.p?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {quick?.mode === "extra" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Açıklama</label>
                <input data-testid="quick-extra-label" value={quickLabel} onChange={(e) => setQuickLabel(e.target.value)} placeholder="Prim, Mesai, Yol…" className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
            )}
            {quick?.mode !== "extra" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Açıklama (isteğe bağlı)</label>
                <input data-testid="quick-desc-input" value={quickLabel} onChange={(e) => setQuickLabel(e.target.value)} placeholder="Örn. 15 Ekim vardiya sayımı" className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Tutar ({quick?.mode === "extra" && quick?.p?.currency === "USD" ? "$" : "₺"})</label>
              <input data-testid="quick-amount" type="number" min="0" step="0.01" value={quickAmount} onChange={(e) => setQuickAmount(e.target.value)} placeholder="0" className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
            </div>
            {quick?.mode !== "extra" && (
              <>
                <div className="flex justify-between text-xs rounded-lg bg-slate-800/60 px-3 py-2" data-testid="quick-current">
                  <span className="text-slate-400">Mevcut toplam</span>
                  <span className="text-white font-mono font-semibold">{fmtTL({ prim: quick?.p?.prim, avans: quick?.p?.avans, kasa: quick?.p?.kasa_acigi }[quick?.mode] || 0)}</span>
                </div>
                <div className="text-xs text-slate-500">"Üzerine Ekle" mevcut tutara ekler, "Değiştir" toplamı girdiğiniz değerle değiştirir. Ay sonu kapanışında sıfırlanır.</div>
              </>
            )}
            {quick?.mode === "extra" ? (
              <button
                data-testid="quick-submit"
                onClick={() => quickM.mutate({ mode: quick.mode, p: quick.p, label: quickLabel, amount: parseFloat(quickAmount || 0) })}
                disabled={!quickAmount || !quickLabel || quickM.isPending}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
              >
                Ekle
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  data-testid="quick-submit-add"
                  onClick={() => quickM.mutate({ mode: quick.mode, p: quick.p, label: quickLabel, amount: parseFloat(quickAmount || 0), op: "add" })}
                  disabled={!quickAmount || quickM.isPending}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
                >
                  Üzerine Ekle
                </button>
                <button
                  data-testid="quick-submit-set"
                  onClick={() => quickM.mutate({ mode: quick.mode, p: quick.p, label: quickLabel, amount: parseFloat(quickAmount || 0), op: "set" })}
                  disabled={quickAmount === "" || quickM.isPending}
                  className="flex-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
                >
                  Değiştir
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ay sonu kapanış onay modalı */}
      {closeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setCloseOpen(false)}>
          <div data-testid="month-close-modal" className="glass rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" /> Ay Sonu Kapanışı
                </h2>
                <p className="text-xs text-slate-400 mt-1">Aşağıdaki değerler geçmiş işlemlere arşivlenip sıfırlanacak. Asgari ücret ve ek maaşlar etkilenmez.</p>
              </div>
              <button data-testid="month-close-cancel-x" onClick={() => setCloseOpen(false)} className="text-slate-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 divide-y divide-slate-800/60" data-testid="month-close-preview-list">
              {prevLoading && <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
              {!prevLoading && previews.length === 0 && <div className="py-8 text-center text-slate-500 text-sm">Kapanış bekleyen kayıt yok.</div>}
              {previews.map((pv) => (
                <div key={pv.personnel_id} data-testid={`month-close-row-${pv.personnel_id}`} className="py-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-white">{pv.name}</span>
                    <span className="text-xs text-slate-500">{fmtPeriod(pv.period_month)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                    {pv.prim > 0 && <span className="text-violet-400">Prim: {fmtTL(pv.prim)}{pv.prim_desc ? ` · ${pv.prim_desc}` : ""}</span>}
                    {pv.avans > 0 && <span className="text-rose-400">Avans: {fmtTL(pv.avans)}{pv.avans_desc ? ` · ${pv.avans_desc}` : ""}</span>}
                    {pv.absent_days > 0 && <span className="text-amber-400">Devamsızlık: {pv.absent_days} gün (-{fmtMoney(pv.absence_deduction, pv.currency)})</span>}
                    {pv.kasa_acigi > 0 && <span className="text-orange-400">Kasa Açığı: {fmtTL(pv.kasa_acigi)}{pv.kasa_desc ? ` · ${pv.kasa_desc}` : ""}</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <button data-testid="month-close-cancel-button" onClick={() => setCloseOpen(false)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold py-2.5 rounded-lg transition-colors">Vazgeç</button>
              <button
                data-testid="month-close-confirm-button"
                onClick={() => closeM.mutate()}
                disabled={closeM.isPending || previews.length === 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
              >
                {closeM.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Onayla ve Sıfırla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Geçmiş işlemler modalı */}
      {histOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setHistOpen(false)}>
          <div data-testid="history-modal" className="glass rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" /> Geçmiş İşlemler
                </h2>
                <p className="text-xs text-slate-400 mt-1">Ay sonu kapanışlarında arşivlenen prim, avans ve devamsızlık kayıtları.</p>
              </div>
              <button data-testid="history-modal-close" onClick={() => setHistOpen(false)} className="text-slate-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-4 space-y-5" data-testid="history-list">
              {closLoading && <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
              {!closLoading && closures.length === 0 && <div className="py-8 text-center text-slate-500 text-sm">Henüz arşivlenmiş kapanış kaydı yok.</div>}
              {Object.entries(closuresByPeriod).map(([period, items]) => (
                <div key={period}>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2" data-testid={`history-period-${period}`}>{fmtPeriod(period)}</div>
                  <div className="divide-y divide-slate-800/60 rounded-xl border border-slate-800/60 overflow-hidden">
                    {items.map((c) => (
                      <div key={c.id} data-testid={`history-row-${c.id}`} className="px-4 py-3 bg-slate-900/40">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-white">{c.name}</span>
                          <span className="text-xs text-slate-500 tabular">Ödenen: {fmtPay(c)}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                          {c.prim > 0 && <span className="text-violet-400">Prim: {fmtTL(c.prim)}{c.prim_desc ? ` · ${c.prim_desc}` : ""}</span>}
                          {c.avans > 0 && <span className="text-rose-400">Avans: {fmtTL(c.avans)}{c.avans_desc ? ` · ${c.avans_desc}` : ""}</span>}
                          {c.absent_days > 0 && <span className="text-amber-400">Devamsızlık: {c.absent_days} gün (-{fmtMoney(c.absence_deduction, c.currency)})</span>}
                          {c.kasa_acigi > 0 && <span className="text-orange-400">Kasa Açığı: {fmtTL(c.kasa_acigi)}{c.kasa_desc ? ` · ${c.kasa_desc}` : ""}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5" />
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-white">{value}</div>
    </div>
  </div>
);
